import React, { useState, useEffect } from 'react';
import { tenantAdminAPI } from '../../../services/api/tenantAdminAPI';
import toast from 'react-hot-toast';
import { 
  Building2, 
  Save, 
  RefreshCw, 
  Upload,
  MapPin,
  Phone,
  Mail,
  Globe,
  Calendar,
  FileText,
  Shield,
  Award,
  Users
} from 'lucide-react';

const OrganizationProfile = () => {
  const [tenantInfo, setTenantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    tenant_type: 'Hospital',
    description: '',
    email: '',
    phone: '',
    website: '',
    address_line1: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'USA',
    license_number: '',
    operating_hours: ''
  });

  useEffect(() => {
    loadTenantInfo();
  }, []);

  const loadTenantInfo = async () => {
    try {
      setLoading(true);
      const response = await tenantAdminAPI.getTenantInfo();
      setTenantInfo(response.data);
      
      // Populate form with existing data
      setFormData({
        name: response.data.name || '',
        tenant_type: response.data.tenant_type || 'Hospital',
        description: response.data.description || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        website: response.data.website || '',
        address_line1: response.data.address_line1 || '',
        city: response.data.city || '',
        state: response.data.state || '',
        postal_code: response.data.postal_code || '',
        country: response.data.country || 'USA',
        license_number: response.data.license_number || '',
        operating_hours: response.data.operating_hours || ''
      });
    } catch (error) {
      console.error('Failed to load tenant info:', error);
      toast.error('Failed to load organization profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await tenantAdminAPI.updateTenant(formData);
      toast.success('Organization profile updated successfully');
      loadTenantInfo(); // Reload to get updated data
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update organization profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#207CE5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0B1C2C]">Organization Profile</h1>
          <p className="text-[#3D3D3D]">Manage your organization's public profile and information</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadTenantInfo}
            className="flex items-center space-x-2 px-4 py-2 border border-[#DCEAF5] rounded-lg hover:bg-[#DCEAF5]"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 px-4 py-2 bg-[#207CE5] text-white rounded-lg hover:bg-[#4682B4] disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Summary Card */}
        <div className="lg:col-span-1">
          <div className="bg-[#F9FBFC] rounded-xl shadow-sm border border-[#DCEAF5] p-6">
            <div className="text-center">
              <div className="w-24 h-24 bg-gradient-to-br from-[#207CE5] to-[#4682B4] rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="h-12 w-12 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-[#0B1C2C]">{formData.name || 'Organization Name'}</h3>
              <p className="text-sm text-[#3D3D3D]">{formData.tenant_type}</p>
              
              <div className="mt-4 space-y-2">
                {formData.email && (
                  <div className="flex items-center justify-center text-sm text-[#3D3D3D]">
                    <Mail className="h-4 w-4 mr-2" />
                    {formData.email}
                  </div>
                )}
                {formData.phone && (
                  <div className="flex items-center justify-center text-sm text-[#3D3D3D]">
                    <Phone className="h-4 w-4 mr-2" />
                    {formData.phone}
                  </div>
                )}
                {formData.website && (
                  <div className="flex items-center justify-center text-sm text-[#3D3D3D]">
                    <Globe className="h-4 w-4 mr-2" />
                    <a href={formData.website} target="_blank" rel="noopener noreferrer" className="text-[#207CE5] hover:text-[#4682B4]">
                      Website
                    </a>
                  </div>
                )}
              </div>

              <button className="mt-4 w-full flex items-center justify-center space-x-2 px-4 py-2 border border-[#DCEAF5] rounded-lg hover:bg-[#DCEAF5]">
                <Upload className="h-4 w-4" />
                <span>Upload Logo</span>
              </button>
            </div>

            {/* Quick Stats */}
            <div className="mt-6 pt-6 border-t border-[#DCEAF5]">
              <h4 className="text-sm font-medium text-[#0B1C2C] mb-3">Organization Stats</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#3D3D3D]">Staff Members</span>
                  <span className="font-medium text-[#0B1C2C]">24</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#3D3D3D]">Active Patients</span>
                  <span className="font-medium text-[#0B1C2C]">156</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#3D3D3D]">Established</span>
                  <span className="font-medium text-[#0B1C2C]">
                    {tenantInfo?.created_at ? new Date(tenantInfo.created_at).getFullYear() : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Form */}
        <div className="lg:col-span-2">
          <div className="bg-[#F9FBFC] rounded-xl shadow-sm border border-[#DCEAF5] p-6">
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-[#0B1C2C] mb-4">Basic Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                      Organization Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                      placeholder="Enter organization name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                      Organization Type
                    </label>
                    <select
                      value={formData.tenant_type}
                      onChange={(e) => setFormData({ ...formData, tenant_type: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                    >
                      <option value="Hospital">Hospital</option>
                      <option value="Clinic">Clinic</option>
                      <option value="Research">Research Center</option>
                      <option value="Telehealth">Telehealth</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                      placeholder="Brief description of your organization"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-medium text-[#0B1C2C] mb-4">Contact Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                      placeholder="organization@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                      Website
                    </label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                      placeholder="https://www.example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div>
                <h3 className="text-lg font-medium text-[#0B1C2C] mb-4">Address Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                      Street Address
                    </label>
                    <input
                      type="text"
                      value={formData.address_line1}
                      onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                      placeholder="123 Main Street"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                      placeholder="City"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                      State / Province
                    </label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                      placeholder="State"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                      placeholder="12345"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                      Country
                    </label>
                    <select
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                    >
                      <option value="USA">United States</option>
                      <option value="CAN">Canada</option>
                      <option value="GBR">United Kingdom</option>
                      <option value="AUS">Australia</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <h3 className="text-lg font-medium text-[#0B1C2C] mb-4">Additional Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                      License Number
                    </label>
                    <input
                      type="text"
                      value={formData.license_number}
                      onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                      placeholder="Medical license number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                      Operating Hours
                    </label>
                    <input
                      type="text"
                      value={formData.operating_hours}
                      onChange={(e) => setFormData({ ...formData, operating_hours: e.target.value })}
                      className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                      placeholder="e.g., Monday-Friday 9:00 AM - 5:00 PM"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Compliance and Certifications */}
      <div className="bg-[#F9FBFC] rounded-xl shadow-sm border border-[#DCEAF5] p-6">
        <h3 className="text-lg font-medium text-[#0B1C2C] mb-4">Compliance & Certifications</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border border-[#DCEAF5] rounded-lg">
            <div className="flex items-center space-x-3">
              <Shield className="h-8 w-8 text-[#008080]" />
              <div>
                <h4 className="font-medium text-[#0B1C2C]">HIPAA Compliant</h4>
                <p className="text-sm text-[#3D3D3D]">Healthcare data protection</p>
              </div>
            </div>
          </div>

          <div className="p-4 border border-[#DCEAF5] rounded-lg">
            <div className="flex items-center space-x-3">
              <Award className="h-8 w-8 text-[#207CE5]" />
              <div>
                <h4 className="font-medium text-[#0B1C2C]">ISO 27001</h4>
                <p className="text-sm text-[#3D3D3D]">Information security management</p>
              </div>
            </div>
          </div>

          <div className="p-4 border border-[#DCEAF5] rounded-lg">
            <div className="flex items-center space-x-3">
              <FileText className="h-8 w-8 text-[#00BCD4]" />
              <div>
                <h4 className="font-medium text-[#0B1C2C]">SOC 2 Type II</h4>
                <p className="text-sm text-[#3D3D3D]">Security and availability</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationProfile;